/**
 * ReceivedNotePage
 * The experience a recipient sees after tapping "Open your note" in their email.
 * Feels like opening a sealed letter: a dark, quiet canvas → a wax-sealed card →
 * the note unfolds → the text reveals like handwriting → the sender is revealed.
 *
 * Route: /n/:token        (open a note)
 *        /n/:token/stop   (one-click opt-out)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { getReceivedNote, blockNoteSender } from '../utils/noteShareApi';
import ReceivedNoteMap from './ReceivedNoteMap';
import './ReceivedNotePage.css';

function WordReveal({ text, reduceMotion }) {
  const words = (text || '').split(/(\s+)/); // keep whitespace tokens
  return (
    <span className="rn-words">
      {words.map((w, i) =>
        w.trim() === '' ? (
          <span key={i}>{w}</span>
        ) : (
          <motion.span
            key={i}
            className="rn-word"
            initial={{ opacity: 0, y: reduceMotion ? 0 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: reduceMotion ? 0 : 0.4 + i * 0.04,
              duration: 0.5,
              ease: 'easeOut',
            }}
          >
            {w}
          </motion.span>
        )
      )}
    </span>
  );
}

export default function ReceivedNotePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const isStopRoute = window.location.pathname.endsWith('/stop');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(false); // seal tapped → map begins its flight
  const [arrived, setArrived] = useState(false); // map settled on the pin → drop the note
  const [mapFailed, setMapFailed] = useState(false); // tiles unavailable → warm-background fallback
  const [revealSender, setRevealSender] = useState(false);

  // One-click opt-out route
  const [stopDone, setStopDone] = useState(false);
  useEffect(() => {
    if (!isStopRoute) return;
    blockNoteSender(token).then(() => setStopDone(true));
  }, [isStopRoute, token]);

  useEffect(() => {
    if (isStopRoute) return;
    let active = true;
    getReceivedNote(token).then((res) => {
      if (!active) return;
      if (res.success) setData(res);
      else setError(res.error || 'This note is no longer available');
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [isStopRoute, token]);

  // Map reached the pin (or we're skipping the flight) — reveal the note, then
  // the sender at the emotional peak. Defined first: the handlers below depend on it.
  const handleArrived = useCallback(() => {
    setArrived((prev) => {
      if (prev) return prev;
      const delay = reduceMotion ? 300 : 2600;
      setTimeout(() => setRevealSender(true), delay);
      return true;
    });
  }, [reduceMotion]);

  // Tapping the seal starts the map's flight; the note itself drops once we arrive.
  const handleOpen = useCallback(() => {
    setOpened(true);
    if (mapFailed) handleArrived(); // no map to fly — reveal straight away
  }, [mapFailed, handleArrived]);

  // Tiles unavailable — drop the map, keep the reveal on the warm background.
  const handleMapError = useCallback(() => {
    setMapFailed(true);
    if (opened) handleArrived();
  }, [opened, handleArrived]);

  // --- opt-out view ---
  if (isStopRoute) {
    return (
      <div className="rn-root rn-center">
        <div className="rn-stop">
          <div className="rn-mark">✦</div>
          <h1>{stopDone ? 'Done.' : 'One moment…'}</h1>
          <p>
            {stopDone
              ? 'You won’t receive Velvynotes at this address again.'
              : 'Updating your preferences.'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rn-root rn-center">
        <motion.div
          className="rn-mark"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          ✦
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rn-root rn-center">
        <div className="rn-stop">
          <div className="rn-mark">✦</div>
          <h1>This note has drifted away.</h1>
          <p>{error}</p>
          <button className="rn-cta-secondary" onClick={() => navigate('/')}>
            Discover Velvynote
          </button>
        </div>
      </div>
    );
  }

  const { note, sender, coverMessage } = data;
  const noteColor = note.color || '#E6B3C2';

  // What the sealed envelope says before opening
  const sealedLine =
    sender.mode === 'identified' && sender.name
      ? `${sender.name.split(/\s+/)[0]} left this for you.`
      : 'Someone left this for you.';

  // The signature after the reveal
  const signature =
    sender.mode === 'anonymous'
      ? '— someone who was thinking of you'
      : sender.name
        ? `— ${sender.name}`
        : '— someone who was thinking of you';

  return (
    <div className={`rn-root${mapFailed ? '' : ' rn-root--map'}`}>
      {!mapFailed && (
        <>
          <ReceivedNoteMap
            lat={note.lat}
            lng={note.lng}
            fly={opened}
            reduceMotion={reduceMotion}
            onArrived={handleArrived}
            onError={handleMapError}
          />
          <div className="rn-map-scrim" aria-hidden="true" />
        </>
      )}
      <AnimatePresence mode="wait">
        {!opened ? (
          /* ---------- SEALED ---------- */
          <motion.div
            key="sealed"
            className="rn-stage"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.5 } }}
          >
            <motion.button
              className="rn-seal-card"
              onClick={handleOpen}
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-label="Open your note"
            >
              <motion.div
                className="rn-seal"
                animate={reduceMotion ? {} : { scale: [1, 1.04, 1] }}
                transition={{ repeat: Infinity, duration: 2.6, ease: 'easeInOut' }}
              >
                ✦
              </motion.div>
            </motion.button>

            <motion.p
              className="rn-sealed-line"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {sealedLine}
            </motion.p>
            <motion.span
              className="rn-open-hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              tap to open
            </motion.span>
          </motion.div>
        ) : arrived ? (
          /* ---------- UNFOLDED ---------- */
          <motion.div
            key="opened"
            className="rn-stage"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className="rn-letter"
              style={{ '--note-color': noteColor }}
              initial={{ rotateX: reduceMotion ? 0 : -82, opacity: 0, y: 24, rotate: 0 }}
              animate={{ rotateX: 0, opacity: 1, y: 0, rotate: -1.6 }}
              transition={{ duration: reduceMotion ? 0.3 : 1.1, ease: [0.16, 1, 0.3, 1] }}
            >
              {coverMessage && (
                <motion.p
                  className="rn-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                >
                  {coverMessage}
                </motion.p>
              )}

              <div className="rn-note-text">
                <WordReveal text={note.text} reduceMotion={reduceMotion} />
              </div>

              <AnimatePresence>
                {revealSender && (
                  <motion.div
                    className="rn-signature"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: 'easeOut' }}
                  >
                    {signature}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Post-reveal growth loop — appears only after the moment lands */}
            <AnimatePresence>
              {revealSender && (
                <motion.div
                  className="rn-after"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.9 }}
                >
                  <p className="rn-after-prompt">Keep this moment going.</p>
                  <div className="rn-after-actions">
                    <button
                      className="rn-cta-primary"
                      onClick={() => navigate('/?from=note&intent=reply')}
                    >
                      Send one back
                    </button>
                    <button
                      className="rn-cta-secondary"
                      onClick={() => navigate('/?from=note&intent=save')}
                    >
                      Save it to your map
                    </button>
                  </div>
                  <button
                    className="rn-cta-text"
                    onClick={() => navigate('/?from=note')}
                  >
                    or start your own map of memories →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
